import { useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { RefreshCw, X } from "lucide-react";

const UPDATE_CHECK_INTERVAL_MS = 60 * 1000; // 60 seconds
const AUTO_RELOAD_SECONDS = 30;

export function PwaUpdatePrompt() {
  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl: string, registration: ServiceWorkerRegistration | undefined) {
      if (!registration) return;
      // Periodically check for new SW versions
      setInterval(() => {
        registration.update().catch(() => {
          // SW script fetch can fail transiently after deployments
        });
      }, UPDATE_CHECK_INTERVAL_MS);
    },
    onRegisterError(error: Error) {
      console.error("[PWA] SW registration error:", error);
    },
  });

  const [countdown, setCountdown] = useState(AUTO_RELOAD_SECONDS);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    if (!needRefresh) return;
    setDismissed(false);
    let remaining = AUTO_RELOAD_SECONDS;
    setCountdown(remaining);
    timerRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        updateServiceWorker(true);
        // Fallback: if SW update doesn't trigger reload within 3s, force reload
        setTimeout(() => window.location.reload(), 3000);
        return;
      }
      setCountdown(remaining);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [needRefresh, updateServiceWorker]);

  function handleReload() {
    clearInterval(timerRef.current);
    updateServiceWorker(true);
    // Fallback: force reload if SW update doesn't trigger it
    setTimeout(() => window.location.reload(), 3000);
  }

  function handleDismiss() {
    clearInterval(timerRef.current);
    setDismissed(true);
  }

  // Auto-dismiss offline-ready toast after 5 seconds
  if (offlineReady) {
    setTimeout(() => setOfflineReady(false), 5000);
  }

  if (dismissed) return null;
  if (!needRefresh && !offlineReady) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <div className="bg-vizimix-800 border border-vizimix-700 rounded-xl p-4 shadow-2xl shadow-black/40">
        {needRefresh ? (
          <>
            <div className="flex items-start gap-3">
              <RefreshCw className="w-5 h-5 text-vizimix-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-vizimix-100">New version available</p>
                <p className="text-xs text-vizimix-400 mt-1">
                  Auto-updating in {countdown}s...
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="text-vizimix-400 hover:text-vizimix-200 shrink-0 -mt-0.5 -mr-1"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-3 ml-8">
              <button
                onClick={handleReload}
                className="px-3 py-1.5 bg-vizimix-600 hover:bg-vizimix-500 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Reload now
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-green-400 rounded-full shrink-0" />
            <p className="text-sm text-vizimix-100">Ready for offline use</p>
          </div>
        )}
      </div>
    </div>
  );
}
